import React, { useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { Container } from '@/components/ui/Container';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { useProjectClient, CLIENT_VIEW } from '@hooks/projects/useProjectClient';
import { useSearchParams } from 'react-router-dom';
import { KPICardsGrid, RolesSectionTitle, RolesSectionSubtitle, ErrorBlock, DetailPanelLoading, ContentCard } from '../style';
import { BoardTabRoot, BoardPageTitle, BoardPageSubtitle } from '../style';

export default function ProjectClientTab({ projectId }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        view,
        setView,
        client,
        rawClient,
        isInhouseClient,
        kpis,
        milestoneColumns,
        milestoneRows,
        changeRequestColumns,
        changeRequestRows,
        paymentColumns,
        paymentRows,
        isLoading,
        isError,
        error,
    } = useProjectClient(projectId);

    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        const fromUrl = String(searchParams.get('clientView') ?? '').toLowerCase();
        if (fromUrl === 'inhouse' && view !== CLIENT_VIEW.INHOUSE) setView(CLIENT_VIEW.INHOUSE);
        if ((fromUrl === 'all' || fromUrl === '') && view !== CLIENT_VIEW.ALL) setView(CLIENT_VIEW.ALL);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleViewChange = (next) => {
        setView(next);
        const nextParams = new URLSearchParams(searchParams);
        if (next === CLIENT_VIEW.INHOUSE) nextParams.set('clientView', 'inhouse');
        else nextParams.delete('clientView');
        setSearchParams(nextParams, { replace: true });
    };

    const clientName = client?.name ?? rawClient?.name ?? '';
    const clientEmail = client?.email ?? rawClient?.email ?? '';
    const clientPhone = client?.phone ?? rawClient?.phone ?? '';

    const isForbidden = useMemo(() => {
        const status = error?.response?.status ?? error?.statusCode ?? null;
        return status === 403;
    }, [error]);

    const filteredMilestoneRows = useMemo(() => {
        const q = String(searchText ?? '').trim().toLowerCase();
        if (!q) return milestoneRows;
        return milestoneRows.filter((r) => String(r?.name ?? '').toLowerCase().includes(q));
    }, [milestoneRows, searchText]);

    const header = (
        <Container $display="flex" $flexDirection="column" $gap="4px">
            <BoardPageTitle>Client</BoardPageTitle>
            <BoardPageSubtitle>Client information, progress and financials for this project.</BoardPageSubtitle>
        </Container>
    );

    if (isLoading) {
        return (
            <Container
                style={{
                    margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <DetailPanelLoading>
                    <Spin size="large" />
                </DetailPanelLoading>
            </Container>
        );
    }

    if (isError) {
        const msg = isForbidden
            ? 'You do not have access to the Client tab for this project.'
            : 'Failed to load client view.';
        return (
            <Container $display="flex" $flexDirection="column" $gap="16px" $width="100%">
                <RolesSectionTitle>Client</RolesSectionTitle>
                <ErrorBlock>{msg}</ErrorBlock>
            </Container>
        );
    }

    return (
        <BoardTabRoot
            style={{
                flex: 1,
                minWidth: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                marginBottom: 0,
                paddingBottom: 0,
            }}
        >
            <BoardTabRoot
                style={{
                    margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                    marginBottom: 0,
                    padding: 0,
                    paddingBottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                }}
            >
                <Container $display="flex" $justifyContent="space-between" $alignItems="flex-start" $gap="16px" $flexWrap="wrap">
                    {header}
                    <Container $display="flex" $alignItems="center" $gap="12px" $flexWrap="wrap">
                        <SliderTab
                            items={[
                                { key: CLIENT_VIEW.ALL, label: 'All' },
                                { key: CLIENT_VIEW.INHOUSE, label: 'Inhouse Client' },
                            ]}
                            activeKey={view}
                            onChange={handleViewChange}
                        />
                    </Container>
                </Container>

                {view === CLIENT_VIEW.INHOUSE && !isInhouseClient ? (
                    <ContentCard $padding="16px" style={{ marginTop: 16 }}>
                        <RolesSectionSubtitle>This project does not have an inhouse (manual) client profile.</RolesSectionSubtitle>
                    </ContentCard>
                ) : (
                    <>
                        <KPICardsGrid $columns={4} $rows={1} $height="auto" style={{ marginTop: 16 }}>
                            <KPICard
                                title="Completion"
                                value={`${kpis.completionPercent}%`}
                                change={{ value: '', isPositive: true, text: 'Milestones completion' }}
                            />
                            <KPICard
                                title="Milestones"
                                value={kpis.milestonesCount}
                                change={{ value: '', isPositive: true, text: 'In this project' }}
                            />
                            <KPICard
                                title="Change Requests"
                                value={kpis.changeRequestsCount}
                                change={{ value: '', isPositive: true, text: 'Total' }}
                            />
                            <KPICard
                                title="Payments"
                                value={kpis.paymentsCount}
                                change={{ value: '', isPositive: true, text: `Total amount ${kpis.paymentsTotal}` }}
                            />
                        </KPICardsGrid>

                        <ContentCard $padding="16px" style={{ marginTop: 16 }}>
                            <Container $display="flex" $flexDirection="column" $gap="6px">
                                <RolesSectionTitle style={{ fontSize: 18 }}>Client Profile</RolesSectionTitle>
                                <RolesSectionSubtitle>
                                    {clientName || clientEmail ? (
                                        <>
                                            <span style={{ fontWeight: 600 }}>{clientName || 'Unnamed Client'}</span>
                                            {clientEmail ? <span>{` • ${clientEmail}`}</span> : null}
                                            {clientPhone ? <span>{` • ${clientPhone}`}</span> : null}
                                        </>
                                    ) : (
                                        'No client linked to this project yet.'
                                    )}
                                </RolesSectionSubtitle>
                            </Container>
                        </ContentCard>

                        <Table
                            title="Milestones"
                            columns={milestoneColumns}
                            dataSource={filteredMilestoneRows}
                            searchValue={searchText}
                            onSearchChange={(e) => setSearchText(e?.target?.value ?? '')}
                            showFilter={false}
                            showMore={false}
                            $topMargin
                        />

                        <Table
                            title="Change Requests"
                            columns={changeRequestColumns}
                            dataSource={changeRequestRows}
                            showSearch={false}
                            showFilter={false}
                            showMore={false}
                            $topMargin
                        />

                        <Table
                            title="Payments"
                            columns={paymentColumns}
                            dataSource={paymentRows}
                            showSearch={false}
                            showFilter={false}
                            showMore={false}
                            $topMargin
                        />
                    </>
                )}
            </BoardTabRoot>
        </BoardTabRoot>
    );
}

